import type { Phone } from "@tandemdiabetes/sugarmate-api-react-client"
import { useCreatePhone as useCreatePhoneAPI } from "@tandemdiabetes/sugarmate-api-react-client"
import { useMemo } from "react"
import { useAlerts } from "../alerts/useAlerts"
import { usePhones } from "./usePhones"
import { noticeError } from "src/utils"

export function useCreatePhone(
  // pass a callback that will be awaited before the swr state is mutated
  onSuccess?: (phoneId: Phone["id"]) => Promise<void>,
) {
  const createPhone = useCreatePhoneAPI()
  const {
    mutate,
    data: phones,
  } = usePhones()
  const { mutate: mutateAlerts } = useAlerts()

  return useMemo(
    () => createPhone && (async (phone: Partial<Phone>) => {
      try {
        const response = await createPhone(phone)

        try {
          await onSuccess?.(response.data.phone.id)
        } catch {
          // intentionally empty
        }

        if (phones) {
          const responsePhone = response.data.phone

          // add the newly created phone to the local state
          void mutate([
            responsePhone,
            ...phones,
          ])
        } else {
          void mutate()
        }

        // refresh local state with current alerts from server
        // creating a phone can change what phones are assigned to alerts
        void mutateAlerts()
        return response
      } catch (e) {
        noticeError(
          e,
          "CreatePhone",
          { phone },
        )
        throw e
      }
    }),
    [
      createPhone,
      phones,
      mutate,
      mutateAlerts,
      onSuccess,
    ],
  )
}
